import Vue from 'vue'
import config from '@/config'
import axios from 'axios'
import moment from 'moment'
import alertHelper from '@/helpers/alert'

const state = {
  data: []
}

const getters = {
  get: (state) => {
    return state.data
  },
  getFeeById: (state) => (id) => {
    const fee =  state.data.filter((f) => f.id === id)
    return fee.length > 0 ? fee[0] : {}
  },
}

const actions = {
  async getAll(context) {
    try {
      const selectedHouse = context.rootGetters['HouseList/getSelectedHouse']
      if (!selectedHouse || !selectedHouse.id) {
        context.commit('set', []);
        return;
      }
      const response = await axios.get(config.apiBaseUrl + 'fee', {
        params: {
          house_id: selectedHouse.id,
        },
      })

      if (response.data.errno === 0) {
        let expenses = response.data.data

        if (expenses.length > 0) {
          expenses.sort((a, b) => {
            const momentA = moment(a.fee_date).format('YYYY-MM-DD')
            const momentB = moment(b.fee_date).format('YYYY-MM-DD')

            if (momentB > momentA) {
              return 1
            } else if (momentB < momentA) {
              return -1
            } else {
              return 0
            }
          })
        }
        context.commit('set', expenses)
      } else {
        alertHelper.error(response.data.errmsg)
      }
    } catch (error) {
      alertHelper.error(error.toString())
    }
  },
  async save(context) {
    const fees = context.getters.get

    // We only want to save when the fees is changed
    // and if the hosue has fee data
    if (fees.length > 0) {
      fees.forEach((fee) => {
        // Fee that is processed is not editable so no point in saving them
        if (fee.state === 0 || (fee.state === 1 && fee.payment_id === null)) {
          if (fee.id) {
            context.dispatch('update', fee)
          } else {
            context.dispatch('add', fee)
          }
        }
      })
    }
  },
  async add(context, fee) {
    try {
      const response = await axios.post(config.apiBaseUrl + 'fee', fee)

      if (response.data.errno === 0) {
        context.commit('add', Object.assign(fee, response.data.data))
        // TODO: Return this data so the view knows about this new id
        // We will not need this once we have routes for fees
        return response.data.data.id
      } else {
        alertHelper.error(response.data.errmsg)
      }
    } catch (error) {
      alertHelper.error(error.toString())
    }
  },
  async update(context, {fee, notify = false}) {
    try {
      // console.log({fee, notify})
      const response = await axios.put(config.apiBaseUrl + 'fee/' + fee.id, fee)

      if (response.data.errno === 0) {
        context.commit('update', fee)

        if (notify) {
          alertHelper.success('Fee succesfully updated')
        }
      } else {
        alertHelper.error(response.data.errmsg)
      }
    } catch (error) {
      alertHelper.error(error.toString())
    }
  },
  /**
   * Deletes fee
   * Makes the API call to delete the fee in database if id exists
   * Deletes in the store if id is empty
   *
   * @param  {object}
   * @param  {object} fee
   */
  async delete(context, fee) {
    // console.log({delete: fee})
    try {
      if (fee.id) {
        // We have a fee id, delete this in the database
        const response = await axios.delete(config.apiBaseUrl + 'fee', {
          data: [fee.id],
        })

        if (response.data.errno === 0) {
          context.commit('delete', fee.id)
          console.log('delete', {fee})
          if (fee.fee_type_id) {
            alertHelper.success('Fee deleted succesfully')
          }
        } else {
          alertHelper.error(response.data.errmsg)
        }
      } else {
        // No id for this one, delete in local store
        context.commit('delete', fee.id)
      }
    } catch (error) {
      alertHelper.error(error.toString())
    }
  },
  /**
   * Deletes the fee attachment
   *
   * @param  {object}
   * @param  {number} id of the fee
   * @param  {number} id of the file
   */
  async deleteAttachment(context, { feeId, fileId }) {
    if (!confirm('Are you sure?')) {
      return false;
    }
    const url = config.apiBaseUrl + 'fee/deletefile'

    try {
      const response = await axios.post(url, {fee_id: feeId, id: fileId})

      if (response.data.errno === 0) {
        context.commit('deleteAttachment', { feeId, fileId })
        alertHelper.success('Successfully deleted fee file')
        return true
      } else {
        alertHelper.error(response.data.errmsg)
      }
    } catch (error) {
      alertHelper.error(error.toString())
    }
    return false
  },
  async uploadFile(context, data) {
    const formData = new FormData()
    formData.append('file', data.file, data.file.name)
    formData.append('fee_id', data.id)
    formData.append('house_id', data.houseId)

    try {
      const response = await axios.post(
        config.apiBaseUrl + 'fee/uploadFile',
        formData
      )

      if (response.data.errno === 0) {
        alertHelper.success('Document uploaded successfully.')

        let fee = context.getters.getFeeById(data.id)
        fee.files.push(response.data.data)

        context.commit('update', fee)

        return true
      } else {
        alertHelper.error(response.data.errmsg)
      }
    } catch (error) {
      alertHelper.error(error.toString())
    }
    return false
  },
}

const mutations = {
  set(state, fees) {
    state.data = fees
  },
  add(state, fee) {
    state.data.push(fee)
  },
  update(state, fee) {
    // We have to update and sort the array so Ui will be updated twice
    // To avoid re-render, creating a copy, sorting and then updating the store
    const existingFees = JSON.parse(JSON.stringify(state.data))

    existingFees.forEach((d) => {
      if (d.id === fee.id) {
        Object.assign(d, fee)
      }
    })

    existingFees.sort((a, b) => {
      const momentA = moment(a.fee_date).format('YYYY-MM-DD')
      const momentB = moment(b.fee_date).format('YYYY-MM-DD')

      if (momentB > momentA) {
        return 1
      } else if (momentB < momentA) {
        return -1
      } else {
        return 0
      }
    })

    Vue.set(state, 'data', existingFees)
  },
  delete(state, id) {
    state.data.forEach((fee, index) => {
      if (fee.id === id) {
        state.data.splice(index, 1)
      }
    })
  },
  deleteAttachment(state, { feeId, fileId }) {
    state.data.forEach((houseFee) => {
      if (houseFee.id === feeId) {
        houseFee.files.forEach((file, index) => {
          if (file.id === fileId) {
            houseFee.files.splice(index, 1)
          }
        })
      }
    })
  },
}

const storeModule = {
  namespaced: true,
  state: state,
  getters: getters,
  actions: actions,
  mutations: mutations,
}

export default storeModule
